import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatTableDataSource } from '@angular/material/table'
import { ApiService, FileService, InvoiceModel, Patient, ProgressIndicatorService, Transaction, TransactionMethod, TransactionModel } from '@mmx/shared'
import { orderBy } from 'lodash'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

@Component({
  selector: 'mmx-transactions-card',
  templateUrl: './transactions-card.component.html',
  styleUrls: ['./transactions-card.component.scss'],
  standalone: false,
})
export class TransactionsCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() transactions: TransactionModel[]
  @Input() title?: string
  @Input() invoice?: InvoiceModel
  @Input() patient?: Patient
  @ViewChild(MatProgressBar) progressBar: MatProgressBar
  @ViewChild(MatPaginator) paginator: MatPaginator

  TYPE = Transaction.TYPE
  transactionsTable: MatTableDataSource<TransactionModel> = new MatTableDataSource()
  displayedColumns: string[] = [
    'name',
    'createdAt',
    'method',
    'type',
    'status',
    'amount',
    'net',
    'actions',
  ]
  totalCollected?: number
  netTotalCollected?: number

  private onDestroy$ = new Subject<void>()

  constructor(
    private fileService: FileService,
    private progressService: ProgressIndicatorService,
    private restService: ApiService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.transactions) {
      this.setData(changes.transactions.currentValue)
    }
  }

  ngOnInit() {
    this.setData(this.transactions)
    this.transactionsTable.paginator = this.paginator
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  download(transaction: TransactionModel): void {
    this.progressBar.mode = 'indeterminate'

    this.restService
      .getTransactionPDF(transaction.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (resp) => {
          this.fileService.saveAs(resp.data)
          this.progressBar.mode = 'determinate'
        },
        () => {
          this.progressService.snack('Failed to generate PDF')
          this.progressBar.mode = 'determinate'
        },
      )
  }

  send(transaction: TransactionModel): void {
    this.progressBar.mode = 'indeterminate'

    this.restService
      .sendTransactionMessage(this.patient.id, transaction.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () => {
          this.progressService.snack('Transaction successfully sent')
          this.progressBar.mode = 'determinate'
        },
        () => {
          this.progressService.snack('Failed to send transaction')
          this.progressBar.mode = 'determinate'
        },
      )
  }

  private setData(transactions: TransactionModel[]) {
    this.transactionsTable.data = orderBy(
      transactions ?? [],
      'createdAt',
    ).filter((t) => t.status !== Transaction.STATUS.VOIDED)
    this.setTotalCollected()
  }

  private setTotalCollected() {
    let grossTotal = 0
    let netTotal = 0

    for (const transaction of this.transactionsTable.data) {
      if (transaction.status === Transaction.STATUS.FAILED) {
        continue
      }

      if (
        transaction.type === Transaction.TYPE.PAYMENT ||
        (transaction.type === Transaction.TYPE.DISPUTE && transaction.status === Transaction.STATUS.SUCCESS)
      ) {
        grossTotal += transaction.amount
        netTotal += transaction.details?.balance?.net ?? transaction.amount
      } else if (transaction.type === Transaction.TYPE.REFUND) {
        // for historic purposes, refunds are stored as a positive number, not a negative number
        grossTotal -= transaction.amount
        // for cash payments gross and net are the same and transaction.details is null
        // we need to negate the amount to get the total net amount for refunds
        netTotal += transaction.details?.balance?.net ?? (transaction.method === TransactionMethod.CASH) ? -transaction.amount : 0
      } else if (transaction.type === Transaction.TYPE.DISPUTE) {
        grossTotal += transaction.amount
        netTotal += transaction.details?.balance?.net ?? 0
      }
    }

    this.totalCollected = grossTotal
    this.netTotalCollected = netTotal
  }
}
